import { Fragment, useCallback, useState, useRef } from 'react'
import classNames from 'classnames'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form/dist/index.ie11'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faCheck } from '@fortawesome/pro-regular-svg-icons'
import { formatNumber } from '../utils'
import formatError from '../../../utilities/formatError'
import { Stack } from '../../stack'
import { Button } from '../../button'
import { TextField } from '../../textfield'
import { Alert } from '../../alert'
import { Row, Col } from '../../grid'
import { Heading } from '../../typography/Heading'
import computeOffsetTop from '../../../utilities/computeTopOffset'
import useEnabledOnClient from '../../../utilities/useEnabledOnClient'
import * as styles from '../CalculatorModule.module.scss'

const validationSchema = {
  residence: {
    min: 0,
    max: 999999999,
  },
  vacation: {
    min: 0,
    max: 999999999,
  },
  furnishings: {
    min: 0,
    max: 999999999,
  },
  autos: {
    min: 0,
    max: 999999999,
  },
  realEstate: {
    min: 0,
    max: 999999999,
  },
  art: {
    min: 0,
    max: 999999999,
  },
  stocks: {
    min: 0,
    max: 999999999,
  },
  varAnnuities: {
    min: 0,
    max: 999999999,
  },
  limitedPartner: {
    min: 0,
    max: 999999999,
  },
  business: {
    min: 0,
    max: 999999999,
  },
  fixedAnnuities: {
    min: 0,
    max: 999999999,
  },
  trustDeeds: {
    min: 0,
    max: 999999999,
  },
  otherFixed: {
    min: 0,
    max: 999999999,
  },
  govtBonds: {
    min: 0,
    max: 999999999,
  },
  muniBonds: {
    min: 0,
    max: 999999999,
  },
  corpBonds: {
    min: 0,
    max: 999999999,
  },
  faceCertificates: {
    min: 0,
    max: 999999999,
  },
  debtMutuals: {
    min: 0,
    max: 999999999,
  },
  checking: {
    min: 0,
    max: 999999999,
  },
  savings: {
    min: 0,
    max: 999999999,
  },
  moneyMarket: {
    min: 0,
    max: 999999999,
  },
  cd: {
    min: 0,
    max: 999999999,
  },
  otherCash: {
    min: 0,
    max: 999999999,
  },
  homeMortgage: {
    min: 0,
    max: 999999999,
  },
  otherMortgage: {
    min: 0,
    max: 999999999,
  },
  autoLoan: {
    min: 0,
    max: 999999999,
  },
  bankLoan: {
    min: 0,
    max: 999999999,
  },
  personalLoan: {
    min: 0,
    max: 999999999,
  },
  creditCards: {
    min: 0,
    max: 999999999,
  },
  otherDebts: {
    min: 0,
    max: 999999999,
  },
}

const PFNetWorthCalculator = () => {
  const [t, i18n] = useTranslation()
  const [language, countryCode] = i18n.language.split('-')
  const [calcResults, setCalcResults] = useState()
  const [storedFormData, setStoredFormData] = useState()
  // Our API doesn’t support sync form submissions and so we need Javascript to submit the form asynchronously by issuing an XHR. Therefore, we need to ensure that the button is initially disabled and gets re-enabled once all Javascript has loaded on the client.
  const disabled = useEnabledOnClient()

  const fieldGroups = {
    assets: [
      {
        label: t('PFNetWorthCalculator.tangibleAssetsLabel', {
          defaultValue: 'Tangible assets',
        }),
        totalName: 'totalTangibleAssets',
        fields: [
          {
            label: t('PFNetWorthCalculator.residenceLabel', {
              defaultValue: 'Residence ($)',
            }),
            name: 'residence',
          },
          {
            label: t('PFNetWorthCalculator.vacationHomeLabel', {
              defaultValue: 'Vacation home ($)',
            }),
            name: 'vacation',
          },
          {
            label: t('PFNetWorthCalculator.furnishingsLabel', {
              defaultValue: 'Furnishings ($)',
            }),
            name: 'furnishings',
          },
          {
            label: t('PFNetWorthCalculator.automobilesLabel', {
              defaultValue: 'Automobiles ($)',
            }),
            name: 'autos',
          },
          {
            label: t('PFNetWorthCalculator.rentalRealEstateLabel', {
              defaultValue: 'Rental real estate ($)',
            }),
            name: 'realEstate',
          },
          {
            label: t('PFNetWorthCalculator.valuablesLabel', {
              defaultValue: 'Art, jewelry, and other valuables ($)',
            }),
            name: 'art',
          },
        ],
      },
      {
        label: t('PFNetWorthCalculator.equityAssetsLabel', {
          defaultValue: 'Equity assets',
        }),
        totalName: 'totalEquityAssets',
        fields: [
          {
            label: t('PFNetWorthCalculator.stocksLabel', {
              defaultValue: 'Stocks ($)',
            }),
            name: 'stocks',
          },
          {
            label: t('PFNetWorthCalculator.variableAnnuitiesLabel', {
              defaultValue: 'Variable annuities ($)',
            }),
            name: 'varAnnuities',
          },
          {
            label: t('PFNetWorthCalculator.limitedPartnershipLabel', {
              defaultValue: 'Limited partnerships ($)',
            }),
            name: 'limitedPartner',
          },
          {
            label: t('PFNetWorthCalculator.businessInterestsLabel', {
              defaultValue: 'Business interests ($)',
            }),
            name: 'business',
          },
        ],
      },
      {
        label: t('PFNetWorthCalculator.fixedPrincipalAssetsLabel', {
          defaultValue: 'Fixed-principal assets',
        }),
        totalName: 'totalFixedPrincipalAssets',
        fields: [
          {
            label: t('PFNetWorthCalculator.fixedDollarAnnuitiesLabel', {
              defaultValue: 'Fixed-dollar annuities ($)',
            }),
            name: 'fixedAnnuities',
          },
          {
            label: t('PFNetWorthCalculator.frustDeedsLabel', {
              defaultValue: 'Trust deeds ($)',
            }),
            name: 'trustDeeds',
          },
          {
            label: t('PFNetWorthCalculator.otherFixedPrincipalAssets', {
              defaultValue: 'Other fixed-principal assets ($)',
            }),
            name: 'otherFixed',
          },
        ],
      },
      {
        label: t('PFNetWorthCalculator.fixedRateAssets', {
          defaultValue: 'Fixed-rate assets',
        }),
        totalName: 'totalFixedRateAssets',
        fields: [
          {
            label: t('PFNetWorthCalculator.governmentBondsLabel', {
              defaultValue: 'U.S. government bonds and securities ($)',
            }),
            name: 'govtBonds',
          },
          {
            label: t('PFNetWorthCalculator.municipalBondsLabel', {
              defaultValue: 'Municipal bonds ($)',
            }),
            name: 'muniBonds',
          },
          {
            label: t('PFNetWorthCalculator.corporateBondsLabel', {
              defaultValue: 'Corporate bonds ($)',
            }),
            name: 'corpBonds',
          },
          {
            label: t('PFNetWorthCalculator.faceAmountCertificatesLabel', {
              defaultValue: 'Face-amount certificates ($)',
            }),
            name: 'faceCertificates',
          },
          {
            label: t('PFNetWorthCalculator.debtMutualFundsLabel', {
              defaultValue: 'Debt mutual funds ($)',
            }),
            name: 'debtMutuals',
          },
        ],
      },
      {
        label: t('PFNetWorthCalculator.cashAndCashEquivalents', {
          defaultValue: 'Cash and cash equivalents',
        }),
        totalName: 'totalCashAssets',
        fields: [
          {
            label: t('PFNetWorthCalculator.checkingAccountsLabel', {
              defaultValue: 'Checking accounts ($)',
            }),
            name: 'checking',
          },
          {
            label: t('PFNetWorthCalculator.savingsAccountsLabel', {
              defaultValue: 'Savings accounts ($)',
            }),
            name: 'savings',
          },
          {
            label: t('PFNetWorthCalculator.moneyMarketFundsLabel', {
              defaultValue: 'Money market funds ($)',
            }),
            name: 'moneyMarket',
          },
          {
            label: t('PFNetWorthCalculator.certificatesOfdepositLabel', {
              defaultValue: 'Certificates of deposit ($)',
            }),
            name: 'cd',
          },
          {
            label: t('PFNetWorthCalculator.otherCashReserveAccountsLabel', {
              defaultValue: 'Other cash reserve accounts ($)',
            }),
            name: 'otherCash',
          },
        ],
      },
    ],
    liabilities: [
      {
        label: t('PFNetWorthCalculator.liabilitiesLabel', {
          defaultValue: 'Liabilities',
        }),
        totalName: 'totalLiabilities',
        fields: [
          {
            label: t('PFNetWorthCalculator.homeMortgageLabel', {
              defaultValue: 'Home mortgage ($)',
            }),
            name: 'homeMortgage',
          },
          {
            label: t('PFNetWorthCalculator.otherMortgageLabel', {
              defaultValue: 'Other mortgage ($)',
            }),
            name: 'otherMortgage',
          },
          {
            label: t('PFNetWorthCalculator.automobileLoansLabel', {
              defaultValue: 'Automobile loans ($)',
            }),
            name: 'autoLoan',
          },
          {
            label: t('PFNetWorthCalculator.BankloansLabel', {
              defaultValue: 'Bank loans ($)',
            }),
            name: 'bankLoan',
          },
          {
            label: t('PFNetWorthCalculator.personalLoansLabel', {
              defaultValue: 'Personal loans ($)',
            }),
            name: 'personalLoan',
          },
          {
            label: t('PFNetWorthCalculator.ChargeAccountDebtLabel', {
              defaultValue: 'Charge account debt ($)',
            }),
            name: 'creditCards',
          },
          {
            label: t('PFNetWorthCalculator.otherDebtsLabel', {
              defaultValue: 'Other debts ($)',
            }),
            name: 'otherDebts',
          },
        ],
      },
    ],
  }
  const { register, errors, handleSubmit } = useForm({
    mode: 'onBlur',
    defaultValues: {
      residence: 0,
      vacation: 0,
      furnishings: 0,
      autos: 0,
      realEstate: 0,
      art: 0,
      stocks: 0,
      varAnnuities: 0,
      limitedPartner: 0,
      business: 0,
      fixedAnnuities: 0,
      trustDeeds: 0,
      otherFixed: 0,
      govtBonds: 0,
      muniBonds: 0,
      corpBonds: 0,
      faceCertificates: 0,
      debtMutuals: 0,
      checking: 0,
      savings: 0,
      moneyMarket: 0,
      cd: 0,
      otherCash: 0,
      homeMortgage: 0,
      otherMortgage: 0,
      autoLoan: 0,
      bankLoan: 0,
      personalLoan: 0,
      creditCards: 0,
      otherDebts: 0,
    },
  })

  const [inProgress, setInProgress] = useState(false)
  const [alert, showAlert] = useState(null)
  const dismissAlert = useCallback(() => showAlert(null), [])
  const resultsContainer = useRef(null)
  const resultsHeader = useRef(null)

  const handleSubmitValid = async formData => {
    setInProgress(true)
    showAlert(false)
    setStoredFormData(formData)

    try {
      const url = 'https://www.calcxml.com/rest/bud07'

      // Documentation
      // https://www.calcxml.com/documentation/rest/Bud07JSONRequest.htm

      const payload = {
        username: process.env.CALCXML_USERNAME,
        password: process.env.CALCXML_PASSWORD,
        language,
        countryCode,
        chartWidth: 900,
        chartHeight: 400,
        barColor1: '63B5E4',
        barColor2: '84BD00',
        barColor3: 'FCC841',
        // barColor4: '#ffffff',
        // barColor5: '#ffffff',
        // barColor6: '#ffffff',
        residence: formData.residence,
        vacation: formData.vacation,
        furnishings: formData.furnishings,
        autos: formData.autos,
        realEstate: formData.realEstate,
        art: formData.art,
        stocks: formData.stocks,
        varAnnuities: formData.varAnnuities,
        limitedPartner: formData.limitedPartner,
        business: formData.business,
        fixedAnnuities: formData.fixedAnnuities,
        trustDeeds: formData.trustDeeds,
        otherFixed: formData.otherFixed,
        govtBonds: formData.govtBonds,
        muniBonds: formData.muniBonds,
        corpBonds: formData.corpBonds,
        faceCertificates: formData.faceCertificates,
        debtMutuals: formData.debtMutuals,
        checking: formData.checking,
        savings: formData.savings,
        moneyMarket: formData.moneyMarket,
        cd: formData.cd,
        otherCash: formData.otherCash,
        homeMortgage: formData.homeMortgage,
        otherMortgage: formData.otherMortgage,
        autoLoan: formData.autoLoan,
        bankLoan: formData.bankLoan,
        personalLoan: formData.personalLoan,
        creditCards: formData.creditCards,
        otherDebts: formData.otherDebts,
      }

      const response = await axios.post(url, payload)

      if (response.status !== 200 || Boolean(response.data.error)) {
        throw new Error(`Unexpected status code: ${response.status} ${response.statusText}`)
      }

      setInProgress(false)
      setCalcResults(response.data)
      window.scrollTo({
        top: computeOffsetTop(resultsContainer.current) - 20,
        behavior: 'smooth',
      })
      resultsHeader.current.focus()
    } catch (error) {
      setInProgress(false)
      showAlert({
        type: 'error',
        title: t('PFNetWorthCalculator.error', {
          defaultValue: 'Error',
        }),
        message: error.message,
      })
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }
  const valueOfLabel = t('PFNetWorthCalculator.valueOf', {
    defaultValue: 'Value of',
  })
  return (
    <div>
      {alert && (
        <Row>
          <Col xs={12}>
            <Alert variant={alert.type} title={alert.title} onDismiss={dismissAlert}>
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}
      <form
        action="/"
        method="POST"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleSubmitValid)}
        className={alert ? styles.HasAlert : ''}
      >
        {fieldGroups.assets.map((group, groupIndex) => (
          <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
            <legend className={styles.Legend}>{group.label}</legend>
            <Row>
              <Col xs={12}>
                <table className={styles.ResultsTable} role="grid">
                  <caption>
                    {group.label}{' '}
                    {t('PFNetWorthCalculator.table', {
                      defaultValue: 'Table',
                    })}
                  </caption>
                  <tbody>
                    <tr role="row">
                      <th scope="col" aria-label="Empty" />
                      <th scope="col">
                        {t('PFNetWorthCalculator.value', {
                          defaultValue: 'Value',
                        })}
                      </th>
                    </tr>
                    {group.fields.map((row, rowIndex) => (
                      <tr key={row.name} role="row">
                        <td>{row.label}</td>
                        <td width="30%">
                          <TextField
                            label={`${valueOfLabel} ${row.label}`}
                            id={row.name}
                            name={row.name}
                            type="number"
                            min="0"
                            ref={register(validationSchema[row.name])}
                            error={formatError(errors[row.name], validationSchema[row.name], t)}
                            autoFocus={groupIndex === 0 && rowIndex === 0}
                            autoSelect
                            visuallyHideLabel
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Col>
            </Row>
          </fieldset>
        ))}

        {fieldGroups.liabilities.map(group => (
          <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
            <legend className={styles.Legend}>{group.label}</legend>
            <Row>
              <Col xs={12}>
                <table className={styles.ResultsTable} role="grid">
                  <caption>
                    {group.label}{' '}
                    {t('PFNetWorthCalculator.table', {
                      defaultValue: 'Table',
                    })}
                  </caption>
                  <tbody>
                    <tr role="row">
                      <th scope="col" aria-label="Empty" />
                      <th scope="col">
                        {t('PFNetWorthCalculator.value', {
                          defaultValue: 'Value',
                        })}
                      </th>
                    </tr>
                    {group.fields.map(row => (
                      <tr role="row">
                        <td>{row.label}</td>
                        <td width="30%">
                          <TextField
                            label={`${valueOfLabel} ${row.label}`}
                            id={row.name}
                            name={row.name}
                            type="number"
                            min="0"
                            ref={register(validationSchema[row.name])}
                            visuallyHideLabel
                            autoSelect
                            error={formatError(errors[row.name], validationSchema[row.name], t)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Col>
            </Row>
          </fieldset>
        ))}

        <Stack alignment="center" className={styles.Submit}>
          <Button
            type="submit"
            disabled={inProgress || disabled}
            loading={inProgress}
            alt={t('PFNetWorthCalculator.calculateAlt', {
              defaultValue: 'Calculate to view results',
            })}
          >
            {t('PFNetWorthCalculator.calculate', {
              defaultValue: 'Calculate',
            })}
          </Button>

          {alert && alert.type === 'error' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faTimes} className={styles.TimesIcon} />
              {t('PFNetWorthCalculator.couldNotCalculate', {
                defaultValue: 'Sorry, could not calculate',
              })}
            </span>
          )}

          {alert && alert.type === 'success' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faCheck} className={styles.CheckIcon} />
              {t('PFNetWorthCalculator.calcualtionComplete', {
                defaultValue: 'Calculation complete. See below for results!',
              })}
            </span>
          )}
        </Stack>
      </form>

      <div ref={resultsContainer} />

      {calcResults && (
        <div className={styles.Results}>
          <Heading
            className={styles.ResultsHeading}
            element="h3"
            size={3}
            tabIndex="0"
            forwardedRef={resultsHeader}
          >
            {t('PFNetWorthCalculator.results', {
              defaultValue: 'Results',
            })}
          </Heading>
          <p>{calcResults.responseText}</p>

          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('PFNetWorthCalculator.summaryTable', {
              defaultValue: 'Summary table',
            })}
          </Heading>

          <table className={styles.ResultsTable} role="grid">
            <caption>
              {t('PFNetWorthCalculator.summaryTable', {
                defaultValue: 'Summary table',
              })}
            </caption>
            <tbody>
              <tr role="row">
                <th colSpan="3" className={styles.ResultsTableHeader}>
                  {t('PFNetWorthCalculator.assets', {
                    defaultValue: 'ASSETS',
                  })}
                </th>
              </tr>
              {fieldGroups.assets.map((group, idx) => (
                <Fragment>
                  {idx === 0 && (
                    <tr role="row">
                      <th scope="col" className="underline">
                        {group.label}
                      </th>
                      <th scope="col" className="underline">
                        {t('PFNetWorthCalculator.amount', {
                          defaultValue: 'Amount',
                        })}
                      </th>
                      <th scope="col" className="underline">
                        {t('PFNetWorthCalculator.percent', {
                          defaultValue: 'Percent',
                        })}
                      </th>
                    </tr>
                  )}
                  {idx > 0 && (
                    <tr role="row">
                      <th scope="col" colSpan="3" className="underline">
                        {group.label}
                      </th>
                    </tr>
                  )}
                  {group.fields.map(row => (
                    <tr role="row">
                      <th scope="row">{row.label.replace('($)', '')}</th>
                      <td>${formatNumber(storedFormData[row.name])}</td>
                      <td>{calcResults[`${row.name}Percent`]}%</td>
                    </tr>
                  ))}
                  <tr role="row">
                    <th scope="row">
                      {t('PFNetWorthCalculator.total', {
                        defaultValue: 'Total',
                      })}{' '}
                      {group.label}
                    </th>
                    <td>{calcResults[group.totalName]}</td>
                    <td>{calcResults[`${group.totalName}Percent`]}%</td>
                  </tr>
                </Fragment>
              ))}
              <tr className={styles.ResultsTableTotalLine} role="row">
                <td>
                  {t('PFNetWorthCalculator.totalAssets', {
                    defaultValue: 'Total assets',
                  })}
                </td>
                <td>{calcResults.totalAssets}</td>
                <td>100%</td>
              </tr>

              <tr role="row">
                <th colSpan="3" className={`${styles.ResultsTableHeader} underline`}>
                  {t('PFNetWorthCalculator.liabilities', {
                    defaultValue: 'LIABILITIES',
                  })}
                </th>
              </tr>
              {fieldGroups.liabilities.map(group => (
                <Fragment>
                  {group.fields.map(row => (
                    <tr role="row">
                      <th scope="row">{row.label.replace('($)', '')}</th>
                      <td>${formatNumber(storedFormData[row.name])}</td>
                      <td>{calcResults[`${row.name}Percent`]}%</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              <tr className={styles.ResultsTableTotalLine} role="row">
                <th scope="row">
                  {t('PFNetWorthCalculator.totalLiabilities', {
                    defaultValue: 'Total liabilities',
                  })}
                </th>
                <td>{calcResults.totalLiabilities}</td>
                <td>100%</td>
              </tr>

              <tr role="row">
                <th className={`${styles.ResultsTableHeader} underline`}>
                  {t('PFNetWorthCalculator.netWorth', {
                    defaultValue: 'NET WORTH',
                  })}
                </th>
                <th className={`${(styles.ResultsTableHeader, styles.ResultsTableLeft)} underline`}>
                  {calcResults.netWorth}
                </th>
                <th aria-label="No value" className="underline" />
              </tr>
            </tbody>
          </table>

          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('PFNetWorthCalculator.graph', {
              defaultValue: 'Graph',
            })}
          </Heading>
          <img
            src={calcResults.chartUrl}
            alt={t('PFNetWorthCalculator.graphOfResults', {
              defaultValue: 'Graph of results',
            })}
          />
        </div>
      )}
    </div>
  )
}

export { PFNetWorthCalculator }
export default PFNetWorthCalculator
